import React, { useEffect, useState } from 'react';
import ArthaGateway from 'service/ArthaGateway';
import { ProductVariant, StockItem } from 'service';
import { Loader } from '../Widgets';

const getToday = () => new Date().toISOString().split('T')[0];

export const StockStatus = () => {
    const [stockItems, setStockItems] = useState<StockItem[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>(getToday());
    const [productVariants, setProductVariants] = useState<ProductVariant[]>([]);
    const [selectedVariantName, setSelectedVariantName] = useState<string>('');
    const [selectedVariantValue1, setSelectedVariantValue1] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

     useEffect(() => {
            let mounted = true;
    
            const initialize = async () => {
                setIsLoading(true);
                try {
                    await loadProductVariants();
                }
                finally {
                    if (mounted) {
                        setIsLoading(false);
                    }
                }
            };
    
            initialize();
    
            return () => {
                mounted = false;
            };
        }, []);

    const loadProductVariants = async (): Promise<ProductVariant[]> => {
            const variantData = localStorage.getItem('productVariants');
            if (variantData) {
                const cachedVariants = JSON.parse(variantData) as ProductVariant[];
                setProductVariants(cachedVariants);
                return cachedVariants;
            }
            try {
                const data = await ArthaGateway.getProductVariants();
                const fetchedVariants = (data || []) as ProductVariant[];
                fetchedVariants.forEach((variant: ProductVariant) => {
                    if (variant.values) {
                        const arrayString = variant.values.replace(/{|}/g, '');
                        if (arrayString) {
                            variant.valueList = JSON.parse(arrayString);
                        }
                    }
                });
                setProductVariants(fetchedVariants);
                localStorage.setItem('productVariants', JSON.stringify(fetchedVariants));
                return fetchedVariants;
            }
            catch (error) {
                console.log(error);
                return [];
            }
        };

    useEffect(() => {
        setIsLoading(true);
        ArthaGateway
            .getStockStatus(selectedDate, selectedVariantName, selectedVariantValue1)
            .then((data) => {
                setStockItems(data || []);
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setIsLoading(false);
            });
    }, [selectedDate, selectedVariantName, selectedVariantValue1]);

    
    const selectedVariant = productVariants.find((variant) => variant.name === selectedVariantName);
    const variantValueOptions = selectedVariant?.valueList || [];

    const formatQuantity = (value: number) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const formatValue = (value: number) => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const statusDefinitions: { key: StockItem['StockStatusPriority']; title: StockItem['StockStatus']; badgeClass: string }[] = [
        { key: 1, title: 'Order Exceeds Stock', badgeClass: 'bg-danger' },
        { key: 2, title: 'Below Minimum Stock (Current or After Order)', badgeClass: 'bg-warning text-dark' },
        { key: 3, title: 'Pending Order - Stock Sufficient', badgeClass: 'bg-info text-dark' },
        { key: 4, title: 'Normal', badgeClass: 'bg-success' },
    ];

    const sections = statusDefinitions.map((status) => ({
        key: status.key,
        title: status.title,
        badgeClass: status.badgeClass,
        entries: stockItems.filter((item) => item.StockStatusPriority === status.key),
    }));

    return (
        <div className="page page-stock-status">
            {isLoading && <Loader />}
            {!isLoading &&
                <>
                    <div className="row mb-2">
                        <h1>Stock Status</h1>
                    </div>

                    <div className="row mb-1">
                        <div className="col-lg-4 col-md-6 mb-3">
                            <label className="form-label mb-0">As On</label>
                            <input className="form-control" type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
                        </div>
                         <div className="col-lg-4 col-md-6 mb-3">
                            <label className="form-label mb-0">Group</label>
                            <select className="form-select" value={selectedVariantName} onChange={(event) => setSelectedVariantName(event.target.value)}>
                                <option value="">All</option>
                                {productVariants.map((variant) => (
                                    <option key={variant.id} value={variant.name}>{variant.name}</option>
                                ))}
                            </select>
                        </div>
                        {selectedVariantName && selectedVariantName !== '' && (
                            <div className="col-lg-4 col-md-6 mb-3">
                                <label className="form-label mb-0">Package</label>
                                <select className="form-select" value={selectedVariantValue1} onChange={(event) => setSelectedVariantValue1(event.target.value)}>
                                    <option value="">All</option>
                                    {variantValueOptions.map((variantValue) => (
                                        <option key={variantValue} value={variantValue}>{variantValue}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="row">
                        {sections.map((section) => (
                            <div className="col-12 col-lg-6 mb-4" key={section.key}>
                                <div className="card card-item w-100">
                                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="fw-bold">{section.title}</div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge ${section.badgeClass}`}>{section.entries.length} items</span>
                                        </div>
                                    </div>
                                    <div className="card-body p-2">
                                        {section.entries.length > 0 ? (
                                            <div className="d-flex flex-column gap-2">
                                                {section.entries.map((item, index) => {
                                                    const orderQty = item.OrderQuantity ?? item.OrderedQuantity ?? 0;
                                                    const orderedPackages = item.OrderedPackages ?? 0;
                                                    const hasOrderQty = orderQty > 0;
                                                    const hasOrderedPackages = orderedPackages > 0;
                                                    const isOrderExceedsStock = section.key === 1;

                                                    return (
                                                        <div className="border rounded px-2 py-2" key={`${section.key}-${item.ProductId}-${item.VariantValue1}-${index}`}>
                                                            <div className="fw-bold w-100 text-truncate" title={item.ProductName}>{item.ProductName}</div>

                                                            <div className="d-flex justify-content-between align-items-start mt-2 gap-2">
                                                                <div className="flex-grow-1">
                                                                    {hasOrderQty && (
                                                                        <div className={`small ${isOrderExceedsStock ? 'text-danger fw-bold' : 'text-secondary'}`}>
                                                                            Order: <span className="fw-semibold">{formatQuantity(orderQty)}</span> {item.Unit} ({formatQuantity(orderedPackages)} box)
                                                                        </div>
                                                                    )}
                                                                    {item.Weight > 0 && (
                                                                        <div className="text-secondary small">Weight: <span className="fw-semibold text-dark">{formatValue(item.Weight)}</span> KG</div>
                                                                    )}
                                                                </div>

                                                                <div className="text-center border rounded bg-light px-2 py-1" style={{ minWidth: 84, minHeight: 50 }}>
                                                                    <div className="fw-bold" style={{ fontSize: '1.45rem', lineHeight: 1.05 }}>{formatQuantity(item.Packages)}</div>
                                                                    <div className="text-uppercase text-muted" style={{ fontSize: '0.6rem', letterSpacing: '0.05em' }}></div>
                                                                        {item.Quantity > 0 && (
                                                                            <div className="small text-secondary mt-1" style={{ lineHeight: 1.05 }}>
                                                                                <b>{formatQuantity(item.Quantity)}</b> {item.Unit}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="desc">No items in this section</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            }
        </div>
    );
};